"use client";

import { c as _c } from "react/compiler-runtime";
import { readFragment } from "@opensea/graphql";
import { HandoffButton } from "@opensea/navigation";
import { classNames, FlexColumn, NumberDisplay, SpaceBetween, TextBody, Tooltip } from "@opensea/ui-kit";
import { differenceInMinutes } from "date-fns";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { LiveTimestamp } from "@/components/activity-feed/LiveTimestamp";
import { slideClasses } from "@/components/collection/overview/styles";
import { itemCardActionVariants } from "@/components/items/card/constants";
import { ItemCard, ItemCardActionButton, ItemCardActionSection, ItemCardActions, ItemCardContent, ItemCardFooter, ItemCardFooterDescription, ItemCardFooterSection, ItemCardMedia, ItemCardName, ItemCardPriceLabel, ItemCardRarityBadge, ItemCardTooltipButtonWrapper } from "@/components/items/card/ItemCard";
import { ItemLink } from "@/components/items/ItemLink";
import { useBuyItems } from "@/components/items/use-buy-items";
import { TokenPrice } from "@/components/price/TokenPrice";
import { EnforcementBadge } from "@/components/trustandsafety/EnforcementBadge";
import { useRowHighlightClassNames } from "@/hooks/use-row-highlight";
import { FeaturedItemSlideFragment } from "./FeaturedItemSlide.fragment";
const SHOW_COUNTDOWN_MINUTES_CUTOFF = 15;
export function FeaturedItemSlide(t0) {
  const $ = _c(17);
  const {
    item: itemKey
  } = t0;
  const item = readFragment(FeaturedItemSlideFragment, itemKey);
  const t = useTranslations("FeaturedItemSlide");
  const highlightClassNames = useRowHighlightClassNames(item.id);
  const [frameTime, setFrameTime] = useState(1);
  const isListed = Boolean(item.bestListing);
  const {
    buyItems,
    disabled: isBuyDisabled,
    disabledReason: buyDisabledReason
  } = useBuyItems([{
    ...item,
    quantity: 1
  }]);
  const renderPriceLabel = function renderPriceLabel() {
    if (item.bestListing) {
      const showCountdown = item.bestListing.endTime ? differenceInMinutes(new Date(item.bestListing.endTime), Date.now()) <= SHOW_COUNTDOWN_MINUTES_CUTOFF : false;
      return <FlexColumn><ItemCardFooterDescription size="sm">{item.enforcement.isCompromised ? <NumberDisplay display="full" /> : <EnforcementBadge entity={item} renderWhenNotEnforced={<TokenPrice className="items-center gap-1.5" price={item.bestListing.pricePerItem} />} />}</ItemCardFooterDescription>{showCountdown || item.lastSale ? <ItemCardFooterDescription className="-mt-1 text-text-secondary">{showCountdown && item.bestListing.endTime ? <TextBody className="text-[12px]">{t("endsIn")}{" "}<span className="font-mono"><LiveTimestamp date={item.bestListing.endTime} display="split" units={{
                hours: false
              }} /></span></TextBody> : null}{!showCountdown && item.lastSale ? <TextBody className="text-[12px]">{t("lastSale")}{" "}<TokenPrice className="text-[11px]" price={item.lastSale} /></TextBody> : null}</ItemCardFooterDescription> : null}</FlexColumn>;
    }
    if (item.lastSale) {
      return <ItemCardPriceLabel component={<TokenPrice price={item.lastSale} />} label={t("lastSale")} size="xs" />;
    }
    if (item.bestOffer) {
      return <ItemCardPriceLabel component={<TokenPrice price={item.bestOffer.pricePerItem} />} label={t("topOffer")} size="xs" />;
    }
    return <ItemCardPriceLabel label={t("notListed")} size="xs" />;
  };
  let t1;
  if ($[0] !== highlightClassNames) {
    t1 = classNames(slideClasses({
      variant: "item"
    }), highlightClassNames);
    $[0] = highlightClassNames;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setFrameTime(undefined);
    t3 = () => setFrameTime(1);
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = classNames("h-[calc(theme(spacing.10)+theme(spacing.3))]", "transition duration-200 ease-out-quint");
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const T0 = ItemCardFooterSection;
  const t5 = "pb-3.5";
  const t6 = renderPriceLabel();
  let t7;
  if ($[5] !== T0 || $[6] !== t6) {
    t7 = <T0 className={t5}>{t6}</T0>;
    $[5] = T0;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== buyDisabledReason || $[9] !== buyItems || $[10] !== isBuyDisabled || $[11] !== isListed || $[12] !== item || $[13] !== t) {
    t8 = isListed ? <Tooltip content={buyDisabledReason} disabled={!isBuyDisabled}><ItemCardTooltipButtonWrapper><ItemCardActionButton disabled={isBuyDisabled} onClick={e => {
          e.stopPropagation();
          e.preventDefault();
          buyItems();
        }}><ItemCardActionSection className={itemCardActionVariants({
            itemView: "grid"
          }).section()}><TextBody size="sm">{t("buyNow")}</TextBody><TokenPrice className={itemCardActionVariants({
              itemView: "grid"
            }).tokenPrice()} price={item.bestListing?.pricePerItem} symbolColor="white" valueColor="white" /></ItemCardActionSection></ItemCardActionButton></ItemCardTooltipButtonWrapper></Tooltip> : null;
    $[8] = buyDisabledReason;
    $[9] = buyItems;
    $[10] = isBuyDisabled;
    $[11] = isListed;
    $[12] = item;
    $[13] = t;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t8) {
    t9 = <ItemCardActions>{t8}</ItemCardActions>;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  return <ItemCard className={t1}><HandoffButton><ItemLink className="flex h-full flex-col" item={item} onPointerEnter={t2} onPointerLeave={t3}><ItemCardContent><ItemCardMedia adjustAspectRatio={true} className="h-full" frameTime={frameTime} item={item} /></ItemCardContent><ItemCardFooter className="min-h-24"><ItemCardFooterSection className={t4}><SpaceBetween className="w-full items-center gap-3"><ItemCardName item={item} />{item.isFungible ? null : <ItemCardRarityBadge item={item} />}</SpaceBetween></ItemCardFooterSection>{t7}</ItemCardFooter>{t9}</ItemLink></HandoffButton></ItemCard>;
}